import { Button, cn } from "@heroui/react";
import { IconTrash } from "@tabler/icons-react";
import React, {
  Dispatch,
  FC,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from "react";
import { DropZone, FileDropItem } from "react-aria-components";
import { CropperModal } from "./cropper-modal";

export const ImageDropzone: FC<{
  files: File[] | null;
  setFiles: Dispatch<SetStateAction<null | File[]>>;
  label?: string;
  existingImageUrl?: string;
  onClear?: () => void;
  onImageChanged: () => void;
}> = ({
        files,
        setFiles,
        label,
        existingImageUrl,
        onClear,
        onImageChanged,
      }) => {
  function convertDataUrlToBlob(dataUrl: string): Blob | string {
    console.log({ dataUrl });
    const arr = dataUrl.split(",");
    const mime = arr[0]?.match(/:(.*?);/)?.[1];
    if (!arr[1]) {
      return dataUrl as string;
    }
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new Blob([u8arr], { type: mime });
  }

  const [backgroundImage, setBackgroundImage] = useState<string | undefined>(
    existingImageUrl
  );
  const [image, setImage] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    console.log({ backgroundImage });
    setFiles(
      backgroundImage
        ? [
          new File(
            [convertDataUrlToBlob(backgroundImage)],
            `profile-image.jpg`
          ),
        ]
        : null
    );
  }, [backgroundImage, setFiles]);

  useEffect(() => {
    if (image) {
      setIsOpen(true);
    }
  }, [image]);

  const handleFileSelect = (inputFiles: FileList | null) => {
    if (!inputFiles || inputFiles.length === 0) return;

    const files = Array.from(inputFiles);
    const images = files.filter(
      (file) => file.type.startsWith("image/") || file.type.startsWith("img/")
    );

    if (images.length > 0) {
      const file = images[0];
      const reader = new FileReader();

      reader.addEventListener(
        "load",
        () => {
          setImage(reader.result as string);
        },
        false
      );

      if (file) {
        reader.readAsDataURL(file);
      }
    }
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <>
      <CropperModal
        isOpen={isOpen}
        image={image!}
        onOpenChange={setIsOpen}
        onSetCroppedImage={(value) => {
          setBackgroundImage(value);
          onImageChanged();
        }}
      />
      <DropZone
        className={cn(
          "border-2 rounded-2xl flex flex-col items-center justify-center relative hover:bg-slate-100 h-48"
        )}
        onDrop={async (e) => {
          const files = e.items.filter(
            (file) =>
              file.kind === "file" &&
              (file.type.startsWith("image/") || file.type.startsWith("img/"))
          ) as FileDropItem[];
          if (files.length > 0) {
            const file = files[0];
            const reader = new FileReader();

            reader.addEventListener(
              "load",
              () => {
                setImage(reader.result as string);
              },
              false
            );

            if (file) {
              reader.readAsDataURL(await file.getFile());
            }
          }
        }}
      >
        {/* Hidden file input */}
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: 'none' }}
          accept="image/*"
          onChange={(e) => handleFileSelect(e.target.files)}
        />

        {/* Display image if available */}
        {backgroundImage && (
          <div className="relative w-full h-full">
            <img
              src={backgroundImage}
              alt="Uploaded"
              className="w-full h-full object-contain"
            />
            {onClear && (
              <Button
                color="danger"
                size="sm"
                className="absolute top-2 right-2"
                onPress={(e) => {
                  setBackgroundImage(undefined);
                  if (onClear) onClear();
                }}
              >
                <IconTrash size={16} />
              </Button>
            )}
          </div>
        )}

        {/* Button to trigger file selection */}
        {!backgroundImage && (
            <Button
              className="w-full h-full"
              variant="flat"
              onPress={handleButtonClick}
            >
              {label}
            </Button>
        )}
      </DropZone>
    </>
  );
};